import { useSeoMeta } from "@unhead/react";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useNavigate } from "react-router-dom";
import type { NostrEvent } from "@nostrify/nostrify";
import { useAuthor } from "@/hooks/useAuthor";
import { useGMContext } from "@/hooks/useGMContext";
import { genUserName } from "@/lib/genUserName";
import { LoginArea } from "@/components/auth/LoginArea";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useMemo } from "react";
import { Sunrise, Loader2, CheckCircle2 } from "lucide-react";
import { nip19 } from "nostr-tools";
import { ThemeToggle } from "@/components/ThemeToggle";
import { MyGMCard } from "@/components/MyGMCard";

interface GMThread {
  originalNote: NostrEvent;
  gmReplies: NostrEvent[];
  isRead: boolean;
  uniqueUsers: string[];
}

const Index = () => {
  const { user } = useCurrentUser();

  // Get GM data from context (persists across navigation)
  const {
    gmNotes,
    userReplies,
    isScanning,
    scanProgress,
    contactsLoaded,
    contactCount,
  } = useGMContext();

  useSeoMeta({
    title: "GM Protocol - Start Your Day with Frens ☀️",
    description:
      "The premier good morning protocol for Nostr. GM your frens every day!",
  });

  // Organize notes into threads (memoized for performance)
  const threads: GMThread[] = useMemo(() => {
    return (
      gmNotes
        .filter((note) => !note.tags.some(([tag]) => tag === "e")) // Only root notes
        .map((note) => {
          // Find all GM replies to this note
          const replies = gmNotes.filter((e) =>
            e.tags.some(([tag, value]) => tag === "e" && value === note.id),
          );

          // Check if user has replied
          const isRead = userReplies.some((e) =>
            e.tags.some(([tag, value]) => tag === "e" && value === note.id),
          );

          // Get unique users who GMed
          const uniqueUsers = Array.from(
            new Set([note.pubkey, ...replies.map((r) => r.pubkey)]),
          );

          return {
            originalNote: note,
            gmReplies: replies,
            isRead,
            uniqueUsers,
          };
        })
        // Sort by most recent only - keep consistent order
        .sort((a, b) => b.originalNote.created_at - a.originalNote.created_at)
    );
  }, [gmNotes, userReplies]);

  // Login view
  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-100 via-amber-100 to-yellow-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
        <div className="absolute top-4 right-4">
          <ThemeToggle />
        </div>
        <Card className="max-w-md w-full border-2 border-amber-300 dark:border-amber-700">
          <CardHeader className="text-center space-y-4">
            <div className="mx-auto w-20 h-20 bg-gradient-to-br from-orange-400 to-amber-500 rounded-full flex items-center justify-center">
              <Sunrise className="w-12 h-12 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                GM Protocol
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                Start every day right with your frens ☀️
              </p>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <p className="text-center text-gray-700 dark:text-gray-300">
              The premier good morning protocol for Nostr. Join the community
              and never miss a GM from your frens.
            </p>
            <LoginArea className="w-full" />
            <div className="text-center text-sm text-muted-foreground">
              Vibed with{" "}
              <a
                href="https://shakespeare.diy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-amber-500"
              >
                Shakespeare
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isLoading = !contactsLoaded;

  // Main inbox view - shows immediately, loads content dynamically
  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-amber-500 rounded-full flex items-center justify-center">
                <Sunrise className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                  GM Protocol
                </h1>
                <p className="text-sm text-muted-foreground">
                  {isLoading ? (
                    "Loading contacts..."
                  ) : isScanning ? (
                    <span className="flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Scanning...{" "}
                      {scanProgress.found > 0 &&
                        `${scanProgress.found} GMs found`}
                    </span>
                  ) : (
                    `${threads.filter((t) => !t.isRead).length} pending GMs`
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <LoginArea className="max-w-60" />
            </div>
          </div>

        </div>

        {/* Focused element: your own GM status for today */}
        <MyGMCard />

        {/* Inbox List */}
        <div className="bg-card border rounded-lg overflow-hidden">
          {/* Loading state for contacts */}
          {isLoading && (
            <div className="divide-y divide-border">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3">
                  <Skeleton className="h-10 w-10 rounded-full shrink-0" />
                  <div className="flex-1 min-w-0 space-y-1.5">
                    <div className="flex items-center gap-2">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-3 w-16 ml-auto" />
                    </div>
                    <Skeleton className="h-3 w-full" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Thread List - shows as they load */}
          {!isLoading && (
            <>
              {threads.length === 0 && !isScanning ? (
                <div className="py-12 px-8 text-center">
                  <p className="text-muted-foreground max-w-sm mx-auto">
                    {contactCount === 0
                      ? "You don't have any contacts yet. Follow some people to see their GMs!"
                      : "No GM notes from your contacts today. Check back soon or GM your frens first! ☀️"}
                  </p>
                </div>
              ) : threads.length === 0 && isScanning ? (
                <div className="divide-y divide-border">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="flex items-center gap-3 px-4 py-3">
                      <Skeleton className="h-10 w-10 rounded-full shrink-0" />
                      <div className="flex-1 min-w-0 space-y-1.5">
                        <div className="flex items-center gap-2">
                          <Skeleton className="h-4 w-24" />
                          <Skeleton className="h-3 w-16 ml-auto" />
                        </div>
                        <Skeleton className="h-3 w-full" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {threads.map((thread) => (
                    <InboxRow key={thread.originalNote.id} thread={thread} />
                  ))}

                  {/* Show scanning indicator at bottom if still scanning */}
                  {isScanning && threads.length > 0 && (
                    <div className="flex items-center justify-center gap-2 py-3 text-sm text-muted-foreground bg-muted/30">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Looking for more GMs...</span>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

function InboxRow({ thread }: { thread: GMThread }) {
  const navigate = useNavigate();
  const author = useAuthor(thread.originalNote.pubkey);
  const metadata = author.data?.metadata;
  const displayName = metadata?.name ?? genUserName(thread.originalNote.pubkey);
  const profileImage = metadata?.picture;

  const noteId = nip19.noteEncode(thread.originalNote.id);

  // Format time relative or as date
  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);

    if (diffHours < 24) {
      return date.toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
      });
    }
    return date.toLocaleDateString([], { month: "short", day: "numeric" });
  };

  // Get other participants (excluding the original author)
  const otherParticipants = thread.uniqueUsers.filter(
    (p) => p !== thread.originalNote.pubkey,
  );

  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-muted/50 ${
        !thread.isRead ? "bg-amber-50/50 dark:bg-amber-950/20" : ""
      }`}
      onClick={() => navigate(`/thread/${noteId}`)}
    >
      {/* Avatar */}
      <Avatar className="h-10 w-10 shrink-0">
        <AvatarImage src={profileImage} alt={displayName} />
        <AvatarFallback>{displayName[0]?.toUpperCase()}</AvatarFallback>
      </Avatar>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Top row: name + participants + time */}
        <div className="flex items-center gap-2">
          <span
            className={`truncate ${!thread.isRead ? "font-semibold text-foreground" : "text-foreground"}`}
          >
            {displayName}
          </span>
          {otherParticipants.length > 0 && (
            <div className="flex items-center shrink-0">
              <ParticipantAvatars pubkeys={otherParticipants.slice(0, 3)} />
              {otherParticipants.length > 3 && (
                <span className="text-xs text-muted-foreground ml-1">
                  +{otherParticipants.length - 3}
                </span>
              )}
            </div>
          )}
          <span className="text-xs text-muted-foreground ml-auto shrink-0">
            {formatTime(thread.originalNote.created_at)}
          </span>
        </div>

        {/* Bottom row: content preview */}
        <p
          className={`text-sm truncate ${!thread.isRead ? "text-foreground" : "text-muted-foreground"}`}
        >
          {thread.originalNote.content}
        </p>
      </div>

      {/* Checkmark on right side */}
      {thread.isRead && (
        <CheckCircle2 className="h-10 w-10 text-green-500 shrink-0" />
      )}
    </div>
  );
}

function ParticipantAvatars({ pubkeys }: { pubkeys: string[] }) {
  return (
    <div className="flex -space-x-1.5">
      {pubkeys.map((pubkey) => (
        <ParticipantAvatar key={pubkey} pubkey={pubkey} />
      ))}
    </div>
  );
}

function ParticipantAvatar({ pubkey }: { pubkey: string }) {
  const author = useAuthor(pubkey);
  const metadata = author.data?.metadata;
  const displayName = metadata?.name ?? genUserName(pubkey);
  const profileImage = metadata?.picture;

  return (
    <Avatar className="h-5 w-5 border border-card">
      <AvatarImage src={profileImage} alt={displayName} />
      <AvatarFallback className="text-[10px]">
        {displayName[0]?.toUpperCase()}
      </AvatarFallback>
    </Avatar>
  );
}

export default Index;
